"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { Bell, Share, PlusSquare, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { createClient } from "@/lib/supabase/client";
import {
  needsMobileInstallGuide,
  isIOS,
  type MobilePlatform,
} from "@/lib/push-helpers";

// DB-based cooldown: don't show the dialog again for this many days after
// the user has dismissed it. sessionStorage would reset on every relogin,
// nagging the user; persisting the timestamp in customer_profiles gives a
// proper grace period across devices and sessions.
const DISMISS_COOLDOWN_DAYS = 7;
const DISMISS_COOLDOWN_MS = DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

interface NotificationConsentDialogProps {
  userId?: string;
}

export function NotificationConsentDialog({
  userId,
}: NotificationConsentDialogProps) {
  const t = useTranslations("profile");
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownChecked, setCooldownChecked] = useState(false);
  const [inCooldown, setInCooldown] = useState(false);
  // Mobile users who haven't installed the PWA get an install guide.
  // iOS: required for push. Android: recommended for better UX.
  const [needsInstall, setNeedsInstall] = useState(false);
  const [platform, setPlatform] = useState<MobilePlatform>("ios");

  useEffect(() => {
    setNeedsInstall(needsMobileInstallGuide());
    setPlatform(isIOS() ? "ios" : "android");
  }, []);
  const {
    isSupported,
    isSubscribed,
    permission,
    subscribe,
    isLoading: isPushLoading,
    autoSubscribeAttempted,
    dbPushEnabled,
  } = usePushNotifications();
  const supabase = createClient();

  // Load the persisted dismissal timestamp once per userId so we can
  // enforce the cooldown before even thinking about opening the dialog.
  useEffect(() => {
    if (!userId) {
      setCooldownChecked(true);
      return;
    }
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("customer_profiles")
        .select("notification_dismissed_at")
        .eq("user_id", userId)
        .single();
      if (!active) return;
      const dismissedAt = data?.notification_dismissed_at
        ? new Date(data.notification_dismissed_at).getTime()
        : 0;
      setInCooldown(
        dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS,
      );
      setCooldownChecked(true);
    })();
    return () => {
      active = false;
    };
  }, [userId, supabase]);

  useEffect(() => {
    if (isPushLoading) return;
    if (!cooldownChecked) return;
    // Wait for auto-subscribe to finish — it handles the "returning user,
    // new device" case by requesting permission directly.
    if (!autoSubscribeAttempted) return;
    // If push is already enabled in DB, auto-subscribe handled it (or tried).
    // No need to show the marketing consent dialog again.
    if (dbPushEnabled === true) return;

    if (!userId) return;
    if (!isSupported) return;
    if (isSubscribed) return;
    if (permission === "denied") return;
    if (inCooldown) return;

    setOpen(true);
  }, [
    userId,
    isSupported,
    isSubscribed,
    permission,
    isPushLoading,
    cooldownChecked,
    inCooldown,
    autoSubscribeAttempted,
    dbPushEnabled,
  ]);

  const handleEnable = async () => {
    setIsLoading(true);
    try {
      const success = await subscribe();

      if (success) {
        if (userId) {
          await supabase
            .from("customer_profiles")
            .update({
              notification_preferences: {
                sms: true,
                push: true,
                email: true,
                events: {
                  reservation_updates: true,
                  chat_messages: true,
                  refund_updates: true,
                  review_reminders: true,
                  crew_updates: true,
                },
              },
              // Clear dismissal timestamp on success so future opt-outs
              // trigger a fresh cooldown.
              notification_dismissed_at: null,
              updated_at: new Date().toISOString(),
            })
            .eq("user_id", userId);
        }
      }

      setOpen(false);
    } catch (error) {
      console.error("Error enabling notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (userId) {
      // Persist dismissal so the dialog doesn't re-appear until cooldown
      await supabase
        .from("customer_profiles")
        .update({
          notification_dismissed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", userId);
    }
    setInCooldown(true);
    setOpen(false);
  };

  // If browser is "denied", subscribe is impossible — don't even show the dialog.
  // (iOS Safari on non-PWA reports "default", so we still show the install guide.)
  if (permission === "denied") return null;
  // Non-iOS without Push API support: nothing to show.
  if (!isSupported && !needsInstall) return null;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={setOpen}
      title={
        needsInstall
          ? t(`notifications.pwa.${platform}.title`)
          : t("notifications.consentTitle")
      }
      description={
        needsInstall
          ? t(`notifications.pwa.${platform}.description`)
          : t("notifications.consentDescription")
      }
      showCloseButton={false}
      footer={
        needsInstall ? (
          <div className="flex w-full">
            <Button
              variant="outline"
              onClick={handleSkip}
              disabled={isLoading}
              className="flex-1"
            >
              {t(`notifications.pwa.gotIt`)}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 w-full">
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={isLoading}
              className="flex-1 text-muted-foreground"
            >
              {t("notifications.skipButton")}
            </Button>
            <Button
              onClick={handleEnable}
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading
                ? t("notifications.enabling")
                : t("notifications.enableButton")}
            </Button>
          </div>
        )
      }
    >
      <div className="flex flex-col items-center text-center">
        {needsInstall ? (
          <div className="mb-4 h-20 w-20 rounded-2xl overflow-hidden shadow-md">
            <Image
              src="/locarora_sq.png"
              alt="Locarora"
              width={80}
              height={80}
              className="h-full w-full object-cover"
              // 로컬 /public 자산 — 정적 CDN 원본 서빙(Vercel 이미지 최적화 과금 제외).
              unoptimized
            />
          </div>
        ) : (
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-8 w-8 text-primary" />
          </div>
        )}
      </div>

      {needsInstall ? (
        // 모바일 유저: "홈 화면에 추가" 3단계 가이드 (iOS/Android 분기)
        <div className="space-y-3 py-4">
          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
            <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
              1
            </div>
            <div className="flex-1">
              <p className="text-sm flex items-center gap-1">
                {t(`notifications.pwa.${platform}.step1`)}
                {platform === "ios" ? (
                  <Share className="h-4 w-4 inline-block text-primary" />
                ) : (
                  <MoreVertical className="h-4 w-4 inline-block text-primary" />
                )}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
            <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
              2
            </div>
            <div className="flex-1">
              <p className="text-sm flex items-center gap-1">
                {t(`notifications.pwa.${platform}.step2`)}
                <PlusSquare className="h-4 w-4 inline-block text-primary" />
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
            <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
              3
            </div>
            <div className="flex-1">
              <p className="text-sm">
                {t(`notifications.pwa.${platform}.step3`)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        // 일반 유저: 혜택 설명
        <div className="space-y-3 py-4">
          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
            <Bell className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium text-sm">
                {t("notifications.consentBenefit1Title")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("notifications.consentBenefit1Description")}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
            <Bell className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium text-sm">
                {t("notifications.consentBenefit2Title")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("notifications.consentBenefit2Description")}
              </p>
            </div>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
}
